import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import { FiCalendar, FiClock } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

/**
 * CustomDatePicker Component
 * Wraps react-datepicker with consistent styling and behavior
 * 
 * @param {Date} selected - The currently selected date
 * @param {Function} onChange - Callback when date changes
 * @param {boolean} showTimeSelect - Whether to show time picker (default: true)
 * @param {boolean} inline - Whether to show inline or as popup (default: false)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional classes
 */
const CustomDatePicker = ({
  selected,
  onChange,
  showTimeSelect = true,
  inline = false,
  placeholder = "Select date and time",
  className = "",
  dateFormat = "MMMM d, yyyy h:mm aa",
  ...props
}) => {

  // Custom Input Component for the Popup Trigger
  const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <div className="relative">
      <input
        type="text"
        readOnly
        className={`w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer bg-white ${className}`}
        onClick={onClick}
        ref={ref}
        value={value}
        placeholder={placeholder}
      />
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
        <SafeIcon icon={FiCalendar} />
      </div>
    </div>
  ));

  return (
    <div className="custom-datepicker-wrapper">
      <DatePicker
        selected={selected}
        onChange={onChange}
        showTimeSelect={showTimeSelect}
        inline={inline}
        dateFormat={dateFormat}
        placeholderText={placeholder}
        customInput={!inline ? <CustomInput /> : null}
        timeIntervals={5}
        timeCaption="Time"
        showPopperArrow={false}
        fixedHeight
        popperPlacement="bottom-start"
        className={inline ? "border border-gray-200 rounded-lg p-4" : ""}
        calendarClassName="shadow-xl border border-gray-200 rounded-lg font-sans"
        dayClassName={() => "rounded hover:bg-blue-50"}
        timeClassName={() => "text-sm"}
        wrapperClassName="w-full"
        {...props}
      />
      <style>{`
        .react-datepicker {
          font-family: inherit;
          border-color: #e5e7eb;
        }
        .react-datepicker__header {
          background-color: #f9fafb;
          border-bottom: 1px solid #e5e7eb;
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          padding-top: 0.75rem;
        }
        .react-datepicker__current-month {
          font-weight: 600;
          color: #111827;
          margin-bottom: 0.5rem;
        }
        .react-datepicker__day-name {
          color: #6b7280;
          font-weight: 500;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background-color: #2563eb !important;
          color: white !important;
        }
        .react-datepicker__time-container {
          border-left: 1px solid #e5e7eb;
        }
        .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected {
          background-color: #2563eb !important;
        }
        .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover {
          background-color: #eff6ff !important;
        }
        .react-datepicker__day:hover {
          background-color: #eff6ff !important;
        }
        .react-datepicker__triangle {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default CustomDatePicker;
